import { createStore } from "zustand/vanilla";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";
import {
  DEFAULT_CARD_BACK,
  CollectionValidationError,
  newCollectionId,
  isPlayableCollection,
  validateCollection,
  type CardCollection,
} from "../shared/collections";
import type { DeckOrder } from "../shared/deck";

export const COLLECTION_STORAGE_ERROR = "Tes jeux restent disponibles pour cette session, mais ce navigateur ne permet pas de les enregistrer. Exporte-les pour les conserver.";
const STORAGE_KEY = "virou-collections-v1";
const DB_NAME = "virou-collections";
const OBJECT_STORE = "games";

export interface CollectionsState {
  collections: CardCollection[];
  selectedId: string | null;
  hydrated: boolean;
  storageError: string | null;
  createCollection(name: string, order: DeckOrder): CardCollection;
  updateCollection(id: string, patch: Partial<Pick<CardCollection, "name" | "order" | "symbols" | "back">>): CardCollection;
  upsertCollection(collection: CardCollection): CardCollection;
  deleteCollection(id: string): void;
  selectCollection(id: string | null): void;
  duplicateCollection(id: string): CardCollection;
}

interface PersistedCollections {
  collections: CardCollection[];
  selectedId: string | null;
}

let database: Promise<IDBDatabase> | undefined;
function openDatabase(): Promise<IDBDatabase> {
  if (!database) {
    database = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === "undefined") { reject(new Error("IndexedDB unavailable")); return; }
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(OBJECT_STORE)) request.result.createObjectStore(OBJECT_STORE);
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); database = undefined; };
        resolve(db);
      };
      request.onerror = () => reject(request.error ?? new Error("IndexedDB unavailable"));
      request.onblocked = () => reject(new Error("IndexedDB blocked"));
    }).catch((error) => { database = undefined; throw error; });
  }
  return database;
}

async function storageTransaction<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(OBJECT_STORE, mode);
    const request = action(transaction.objectStore(OBJECT_STORE));
    // Resolving on transaction completion also catches quota failures after the request succeeds.
    transaction.oncomplete = () => resolve(request.result);
    transaction.onerror = () => reject(transaction.error ?? request.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}

function reportStorageError(): void {
  if (collectionStore.getState().storageError !== COLLECTION_STORAGE_ERROR) {
    collectionStore.setState({ storageError: COLLECTION_STORAGE_ERROR });
  }
}

// Writes are serialized so a slower IndexedDB open/transaction cannot overwrite a newer edit.
let pendingWrite = Promise.resolve();
// Transient flags (hydration/error) must not cause writes, especially after a failed read.
let lastRequestedValue = JSON.stringify({ state: { collections: [], selectedId: null }, version: 1 });
const indexedDbStorage: StateStorage = {
  async getItem(name) {
    await pendingWrite;
    const value = await storageTransaction("readonly", (store) => store.get(name));
    if (typeof value === "string") lastRequestedValue = value;
    return typeof value === "string" ? value : null;
  },
  setItem(name, value) {
    if (value === lastRequestedValue) return pendingWrite;
    lastRequestedValue = value;
    pendingWrite = pendingWrite.then(async () => {
      try { await storageTransaction("readwrite", (store) => store.put(value, name)); }
      catch { reportStorageError(); }
    });
    return pendingWrite;
  },
  async removeItem(name) {
    await pendingWrite;
    try { await storageTransaction("readwrite", (store) => store.delete(name)); }
    catch { reportStorageError(); }
  },
};

function checkedCollection(value: unknown): CardCollection {
  const result = validateCollection(value);
  if (!result.valid || !result.collection) throw new CollectionValidationError(result.errors);
  return result.collection;
}

function collectionById(id: string): CardCollection {
  const collection = collectionStore.getState().collections.find((item) => item.id === id);
  if (!collection) throw new Error("Ce jeu n’existe plus.");
  return collection;
}

export const collectionStore = createStore<CollectionsState>()(persist<CollectionsState, [], [], PersistedCollections>(
  (set, get) => ({
    collections: [], selectedId: null, hydrated: false, storageError: null,
    createCollection(name, order) {
      const now = Date.now();
      const collection = checkedCollection({ id: newCollectionId(), name, order, symbols: [], back: { ...DEFAULT_CARD_BACK }, createdAt: now, updatedAt: now });
      set((state) => ({ collections: [...state.collections, collection] }));
      return collection;
    },
    updateCollection(id, patch) {
      const previous = collectionById(id);
      // Copy only supported fields; untrusted object properties never become store state.
      const value = {
        ...previous,
        ...(patch.name === undefined ? {} : { name: patch.name }),
        ...(patch.order === undefined ? {} : { order: patch.order }),
        ...(patch.symbols === undefined ? {} : { symbols: patch.symbols.map((symbol, index) => ({ ...symbol, id: index })) }),
        ...(patch.back === undefined ? {} : { back: patch.back }),
        updatedAt: Math.max(previous.createdAt, Date.now()),
      };
      return get().upsertCollection(checkedCollection(value));
    },
    upsertCollection(value) {
      const collection = checkedCollection(value);
      set((state) => ({ collections: state.collections.some((item) => item.id === collection.id)
        ? state.collections.map((item) => item.id === collection.id ? collection : item)
        : [...state.collections, collection], selectedId: state.selectedId === collection.id && !isPlayableCollection(collection) ? null : state.selectedId }));
      return collection;
    },
    deleteCollection(id) {
      set((state) => ({ collections: state.collections.filter((item) => item.id !== id), selectedId: state.selectedId === id ? null : state.selectedId }));
    },
    selectCollection(id) {
      if (id !== null && !isPlayableCollection(collectionById(id))) throw new Error("Complète toutes les images du jeu avant de le sélectionner.");
      set({ selectedId: id });
    },
    duplicateCollection(id) {
      const previous = collectionById(id);
      const now = Date.now();
      const collection = checkedCollection({ ...previous, id: newCollectionId(), name: `${previous.name.slice(0, 52)} — copie`, createdAt: now, updatedAt: now });
      set((state) => ({ collections: [...state.collections, collection] }));
      return collection;
    },
  }),
  {
    name: STORAGE_KEY,
    version: 1,
    storage: createJSONStorage(() => indexedDbStorage),
    skipHydration: true,
    partialize: ({ collections, selectedId }) => ({ collections, selectedId }),
    merge(persisted, current) {
      if (typeof persisted !== "object" || persisted === null) return current;
      const candidate = persisted as Partial<PersistedCollections>;
      const collections: CardCollection[] = [];
      const ids = new Set<string>();
      if (Array.isArray(candidate.collections)) {
        for (const value of candidate.collections) {
          const validation = validateCollection(value);
          if (!validation.valid || !validation.collection || ids.has(validation.collection.id)) continue;
          ids.add(validation.collection.id);
          collections.push(validation.collection);
        }
      }
      // Editing before the explicit hydration finishes must never discard those new edits.
      for (const local of current.collections) {
        const index = collections.findIndex((item) => item.id === local.id);
        if (index < 0) collections.push(local);
        else if (local.updatedAt >= collections[index].updatedAt) collections[index] = local;
      }
      const preferredId = current.selectedId ?? candidate.selectedId;
      const selectedId = typeof preferredId === "string" && collections.some((item) => item.id === preferredId && isPlayableCollection(item)) ? preferredId : null;
      return { ...current, collections, selectedId };
    },
    onRehydrateStorage: () => (_state, error) => {
      if (error) reportStorageError();
    },
  },
));

let hydration: Promise<void> | undefined;
export function hydrateCollections(): Promise<void> {
  if (!hydration) {
    hydration = Promise.resolve(collectionStore.persist.rehydrate()).catch(() => { reportStorageError(); }).then(() => {
      collectionStore.setState({ hydrated: true });
    });
  }
  return hydration;
}
